import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Form, Button, message, Typography, Alert, Input, Modal, Steps } from 'antd';
import { SendOutlined, MailOutlined, SafetyOutlined, UserOutlined, CheckCircleOutlined, SaveOutlined } from '@ant-design/icons';
import Wrapper from '../form-editor/Preview/Wrapper';
import moment from 'moment';
import { buildSkippedStepData, getRequestApprovers, getUniqueStepUsers, getVisibleWidgets, getStepFormValues, isObjectEmpty, nodeConditionsHandler, valueModifier } from '../../utils/requestHelper';
import { approvalDelayed, createFormValuesSlicer, flattenFormItems, getAllWidgets, getDueDate, getPreviousStepFrozenFields, getPublicFormItems, getRequestorFields } from '../../utils/formsHelper';
import { getSpaceAllPublicFormsByWorkflowId } from '../../services/requestServices';
import { sendPublicFormOTP, verifyPublicFormOTP, submitPublicFormRequest, submitOngoingFormRequest, saveOngoingFormDraft, uploadPublicFormAttachment } from '../../services/publicFormServices';
import { buildFormPrintFileName } from '../../utils/formPrint';
import { generateStructuredFormPrintFile } from '../../utils/formStructuredPrint';
import { showLoader } from '../../utils/showloader';
import injectDataInTemplate from '../../utils/extractWidget';
import { getFormattedDate } from '../../utils/getLocalFormattedDate';
import { isDateObject } from '../../utils/safeDate.js';
import { useTranslation } from 'react-i18next';
import { resolveFieldTranslation } from '../../utils/formTranslations';
import { resolveNodeTranslation } from '../../utils/workflowTranslations';
import { trimStringValues } from '../../utils/trimStrings';
import { normalizeCompanyIdentifier } from '../../utils/companyIdentifier';
import { sortNodesByEdges } from '../../utils/workflowHelper';

const { Title, Paragraph } = Typography;

const EMPTY_ARR = [];
const EMPTY_OBJ = {};

const getWorkflowNodes = (workflow = {}) => {
    if (Array.isArray(workflow.node)) return workflow.node
    if (Array.isArray(workflow.nodes)) return workflow.nodes
    return []
}

const getWorkflowEdges = (workflow = {}) => {
    if (Array.isArray(workflow.edge)) return workflow.edge
    if (Array.isArray(workflow.edges)) return workflow.edges
    return []
}

const asConditionList = (value) => {
    if (Array.isArray(value)) return value
    if (Array.isArray(value?.result)) return value.result
    if (Array.isArray(value?.data)) return value.data
    return []
}

// Public payloads store conditions on the form, the workflow, or the node.
const collectWorkflowConditions = (formData = {}, workflow = {}, nodes = []) => {
    const bags = [
        formData.workflowConditions,
        formData.conditions,
        workflow.workflowConditions,
        workflow.conditions,
    ]
    nodes.forEach((node) => {
        bags.push(node?.conditions)
        bags.push(node?.data?.conditions)
    })
    const byId = new Map()
    const extras = []
    bags.forEach((bag) => {
        asConditionList(bag).forEach((condition) => {
            if (!condition || typeof condition !== "object") return
            if (condition.conditionId) {
                byId.set(String(condition.conditionId), condition)
            } else {
                extras.push(condition)
            }
        })
    })
    return [...byId.values(), ...extras]
}

const conditionsForNode = (conditions, node) => {
    const ids = [node?.id, node?.data?.id, node?.data?.nodeId, node?.formData?.nodeId]
        .filter((id) => id !== undefined && id !== null && id !== "")
        .map((id) => String(id))
    return conditions.filter((condition) => condition?.nodeId != null && ids.includes(String(condition.nodeId)))
}

const FOCUSABLE_FIELD_SELECTOR = 'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), .ant-select-selector, .ant-picker, [tabindex]:not([tabindex="-1"])';

// Table rows are written with setFieldValue + passFormValues. Spreading
// getFieldsValue / useWatch on top of that map used to replace those rows with
// undefined or "" because Form.Item wrapping antd Table is not a real control
// and useWatch often never sees the imperative write.
const isTableRowArray = (value) =>
    Array.isArray(value) && value.some((row) => row && typeof row === "object" && !Array.isArray(row))

const isAttachmentValue = (value) =>
    value && typeof value === "object" && !Array.isArray(value) && Array.isArray(value.fileList)

const isClearedAttachment = (value) =>
    value === null || (isAttachmentValue(value) && value.fileList.length === 0)

const mergeFormValueMaps = (base = {}, next = {}, options = {}) => {
    const { keepTableRows = false, allowNull = false, preserveClears = false } = options
    const merged = { ...base }
    Object.keys(next || {}).forEach((key) => {
        const incoming = next[key]
        if (incoming === undefined) return
        if (incoming === null) {
            if (isTableRowArray(merged[key])) return
            if (allowNull || isAttachmentValue(merged[key])) {
                merged[key] = null
            }
            return
        }
        if (incoming === "" && Array.isArray(merged[key])) return
        if (isTableRowArray(merged[key]) && !Array.isArray(incoming)) return
        if (keepTableRows && isTableRowArray(merged[key]) && Array.isArray(incoming) && incoming.length === 0) return
        if (preserveClears && isClearedAttachment(merged[key]) && isAttachmentValue(incoming) && incoming.fileList.length > 0) return
        merged[key] = incoming
    })
    return merged
}

const expandCollapsedAncestors = (el) => {
    const collapsedItems = [];
    let collapseItem = el?.closest?.('.ant-collapse-item');
    while (collapseItem) {
        if (!collapseItem.classList.contains('ant-collapse-item-active')) {
            collapsedItems.push(collapseItem);
        }
        collapseItem = collapseItem.parentElement?.closest('.ant-collapse-item');
    }
    collapsedItems.reverse().forEach((item) => {
        const expandIcon = item.querySelector(':scope > .ant-collapse-header .ant-collapse-expand-icon');
        const header = item.querySelector(':scope > .ant-collapse-header');
        (expandIcon || header)?.click();
    });
    return collapsedItems.length > 0;
};

const scrollToFirstMissingField = (form, errorInfo) => {
    const firstFieldName = errorInfo?.errorFields?.[0]?.name;

    window.setTimeout(() => {
        const errorItem = document.querySelector('.ant-form-item-has-error');
        const didExpand = errorItem ? expandCollapsedAncestors(errorItem) : false;

        const scrollIntoPlace = () => {
            const target = document.querySelector('.ant-form-item-has-error') || errorItem;
            if (target && typeof target.scrollIntoView === 'function') {
                target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const focusable = target.querySelector(FOCUSABLE_FIELD_SELECTOR);
                if (focusable && typeof focusable.focus === 'function') {
                    focusable.focus({ preventScroll: true });
                }
                return;
            }
            if (firstFieldName && form?.scrollToField) {
                form.scrollToField(firstFieldName, { behavior: 'smooth', block: 'center' });
            }
        };

        if (didExpand) {
            window.setTimeout(scrollIntoPlace, 250);
        } else {
            scrollIntoPlace();
        }
    }, 50);
};

const PublicForm = ({ formData, shareToken, onSubmissionSuccess, userInfo = {}, notificationTemplates = [], requestId = "", tenantId = "", caseId = "", setDocumentId, ensureRequestRecord, type = "", defaultFormValues = null, language = "" }) => {
    const [form] = Form.useForm();
    const [emailForm] = Form.useForm();
    const [otpForm] = Form.useForm();

    const [submitting, setSubmitting] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [draftSavedOn, setDraftSavedOn] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    // const [formValues, setFormValues] = useState({});

    // Email verification states
    const [showEmailVerification, setShowEmailVerification] = useState(false);
    const [currentStep, setCurrentStep] = useState(0); // 0: email, 1: otp, 2: submit
    const [userEmail, setUserEmail] = useState('');
    const [fullName, setFullName] = useState("")
    const [sendingOTP, setSendingOTP] = useState(false);
    const [verifyingOTP, setVerifyingOTP] = useState(false);
    const [verificationToken, setVerificationToken] = useState('');

    const { t, i18n } = useTranslation();
    // The language the share link was created with wins over the viewer's own app
    // locale, so a recipient always sees the form in the language it was sent in.
    const contentLanguage = language || i18n.language;
    const formElements = useMemo(
        () => getPublicFormItems(formData?.items || EMPTY_ARR),
        [formData?.items]
    )
    const formHeader = resolveFieldTranslation(formData?.formHeader || {}, contentLanguage);
    const spaceData = formData?.spaceData || EMPTY_OBJ;
    const formConditions = formData?.formConditions || EMPTY_ARR
    const workflow = formData?.workflow || EMPTY_OBJ
    const workflowNodes = useMemo(() => getWorkflowNodes(workflow), [workflow])
    const workflowEdges = useMemo(() => getWorkflowEdges(workflow), [workflow])
    const workflowConditions = useMemo(
        () => collectWorkflowConditions(formData, workflow, workflowNodes),
        [formData, workflow, workflowNodes]
    )
    // Conditions compare against the node that owns this form (same as a live
    // request on that step). Fall back to the requester node when the form is not
    // attached to a step, e.g. an intake share before the workflow payload lands.
    const currentStatus = (
        workflowNodes.find(node => node.formData?.formId === formData?.formId)
        || workflowNodes.find(node => node.type === "default")
        || {}
    ).id || ""
    const freezeFields = useMemo(() => {
        const requestWorkflow = formData?.request?.workflow
            || formData?.requestDetail?.workflow
            || formData?.workflowHistory
            || (Array.isArray(formData?.workflow) ? formData.workflow : [])
        return getPreviousStepFrozenFields(requestWorkflow)
    }, [formData])
    const previousFormItems = useMemo(
        () => flattenFormItems(formData?.previousForms),
        [formData?.previousForms]
    )
    const previousFormValues = useMemo(() => {
        const collected = {}
        ;(formData?.previousForms || []).forEach((formEntry) => {
            const defaults = formEntry?.defaultValues
            const answers = formEntry?.formValues
            Object.assign(collected, typeof defaults === "object" && defaults ? defaults : {})
            if (answers && typeof answers === "string") {
                try {
                    const parsed = JSON.parse(answers)
                    if (parsed && typeof parsed === "object") Object.assign(collected, parsed)
                } catch (error) {
                    // ignore malformed stored values
                }
            } else if (answers && typeof answers === "object") {
                Object.assign(collected, answers)
            }
        })
        return collected
    }, [formData?.previousForms])
    const allFormElements = useMemo(() => {
        const fromWorkflow = flattenFormItems(formData?.workflowForms)
        const current = fromWorkflow.length ? fromWorkflow : (formData?.items || [])
        return [...previousFormItems, ...current]
    }, [formData?.workflowForms, formData?.items, previousFormItems])
    // Do not default this to `{}`. Wrapper treats a truthy formValues object as
    // "conditions may run". An empty snapshot then hides/unmounts fields (hide
    // when not equal, hideInitially without show) and the store loses the answers.
    const watchedValues = Form.useWatch([], { form, preserve: true });
    // useWatch([], { preserve: true }) can stay undefined/{} even after the user
    // types. Imperative setFieldValue also often skips that snapshot. Keep every
    // onValuesChange so submit still has what was filled in.
    const filledValuesRef = useRef({});
    const [filledValues, setFilledValues] = useState({});
    const sliceRef = useRef(null);
    if (!sliceRef.current) sliceRef.current = createFormValuesSlicer();
    const mergeFilledValues = useCallback((next = {}) => {
        const merged = mergeFormValueMaps(filledValuesRef.current, next, { allowNull: true });
        const prev = filledValuesRef.current;
        const nextKeys = Object.keys(merged);
        if (Object.keys(prev).length === nextKeys.length && nextKeys.every((key) => prev[key] === merged[key])) {
            return;
        }
        filledValuesRef.current = merged;
        setFilledValues(merged);
    }, []);
    const getLiveFormValues = () => {
        let collected = mergeFormValueMaps({}, filledValuesRef.current)
        collected = mergeFormValueMaps(collected, form.getFieldsValue() || {}, { keepTableRows: true, preserveClears: true })
        collected = mergeFormValueMaps(collected, form.getFieldsValue(true) || {}, { keepTableRows: true, preserveClears: true })
        collected = mergeFormValueMaps(collected, watchedValues || {}, { keepTableRows: true, preserveClears: true })
        getAllWidgets(formData?.items || formElements).forEach((widget) => {
            if (!widget?.id) return
            if (widget.formType === "tableForm") {
                const filled = filledValuesRef.current?.[widget.id]
                const storeValue = form.getFieldValue(widget.id)
                const current = collected[widget.id]
                if (isTableRowArray(current)) return
                if (isTableRowArray(storeValue)) {
                    collected[widget.id] = storeValue
                    return
                }
                if (isTableRowArray(filled)) {
                    collected[widget.id] = filled
                    return
                }
                if (Array.isArray(storeValue)) collected[widget.id] = storeValue
                else if (Array.isArray(filled)) collected[widget.id] = filled
                return
            }
            const existing = collected[widget.id]
            if (existing !== undefined && existing !== null && existing !== "") return
            const storeValue = form.getFieldValue(widget.id)
            if (storeValue !== undefined) collected[widget.id] = storeValue
        })
        return collected
    }
    const liveFormValues = mergeFormValueMaps(filledValues, watchedValues || {}, { keepTableRows: true, preserveClears: true });
    const formValuesForConditions = {
        ...previousFormValues,
        ...(formData?.request?.value && typeof formData.request.value === "object" ? formData.request.value : {}),
        ...(formData?.requestDetail?.value && typeof formData.requestDetail.value === "object" ? formData.requestDetail.value : {}),
        ...liveFormValues,
    }
    const formValues = Object.keys(formValuesForConditions).length > 0 ? formValuesForConditions : undefined;
    // Only an ongoing link shows the case ID on screen. A first-time submitter has
    // no request yet, and the one minted for them while they fill the form is an
    // internal draft — the reference is theirs only once the form is submitted.
    const displayCaseId = type === "ongoing"
        ? (caseId || formData?.caseId || formData?.request?.caseId || formData?.requestDetail?.caseId || "")
        : ""
    // Picker selections from earlier steps. Used only to resolve user-picker
    // approvers — never seeded into this form or stored as this step's values.
    const previousPickerValues = formData?.previousValues || {}

    // useEffect(() => {
    //     setFormValues(watchedValues || {});
    // }, [watchedValues]);

    useEffect(() => {
        // Seed only this step's stored values. previousValues holds earlier
        // picker selections for approver lookup and must not prefill the form.
        if (formData?.formValues) {
            let formattedValues = valueModifier(formData.formValues)
            const next = { ...(defaultFormValues || {}), ...formattedValues }
            form.setFieldsValue(next)
            mergeFilledValues(next)
        } else if (defaultFormValues) {
            form.setFieldsValue({ ...defaultFormValues })
            mergeFilledValues(defaultFormValues)
        }
    }, [formData?.formValues, defaultFormValues])

    // Handle email submission and OTP sending
    const handleEmailSubmission = async (values) => {
        try {
            values = trimStringValues(values);
            const { email, name } = values;

            setUserEmail(email);
            setFullName(name)

            setSendingOTP(true);

            if (!requiresOTPVerification) {
                setShowEmailVerification(false);
                setVerificationToken('tracker-bypass');
                const currentValues = getLiveFormValues();
                await handleFormSubmission(currentValues, { name, email });
                return;
            }

            const response = await sendPublicFormOTP(email, shareToken);

            if (response.status === 200) {
                setCurrentStep(1);
                message.success(t('OTP sent to your email successfully!'));
            } else {
                throw new Error(response.message || t('Failed to send OTP'));
            }
        } catch (error) {
            message.error(error.message || t('Failed to send OTP. Please try again.'));
        } finally {
            setSendingOTP(false);
        }
    };

    // Handle OTP verification
    const handleOTPVerification = async (values) => {
        try {
            setVerifyingOTP(true);
            values = trimStringValues(values);
            const { otp } = values;

            const response = await verifyPublicFormOTP(userEmail, otp, fullName, shareToken);
            const verified = response.success === true || response.data?.result?.otpVerified === true;

            if (verified) {
                setVerificationToken(response.data?.verificationToken || 'verified');
                setCurrentStep(2);
                message.success(t('Email verified successfully!'));
            } else {
                throw new Error(response.message || t('OTP verification failed'));
            }
        } catch (error) {
            message.error(error.message || t('Invalid OTP. Please try again.'));
        } finally {
            setVerifyingOTP(false);
        }
    };

    // Build a single notification payload for a template, mirroring the shape
    // produced in triggerNotification (requests/index.jsx). The backend dispatches
    // each entry after the request is created.
    const buildNotificationPayload = (template, recipients, notificationParams, redirectUrl, formItems = [], subjectOverride = "") => {
        let _notificationParams = { ...notificationParams, redirectUrl }
        let emailLink = window.location.origin + redirectUrl + "?redirectFrom=mail"
        let spaceId = notificationParams["spaceId"]

        Object.keys(_notificationParams).forEach(value => {
            let currentValue = _notificationParams[value] || ""

            if (Array.isArray(currentValue)) {
                _notificationParams[value] = currentValue.join(", ")
            } else if (typeof currentValue === "object" && (currentValue.user || currentValue.group || currentValue.userPicker)) {
                let userWidgetValues = [];
                if (currentValue.user?.length > 0) {
                    userWidgetValues.push(currentValue.user)
                }
                if (currentValue.group?.length > 0) {
                    userWidgetValues.push(currentValue.group)
                }
                _notificationParams[value] = userWidgetValues.join(", ")
            }
            if (isDateObject(currentValue) && value !== "Request Id" && value !== "Chatroom message") {
                _notificationParams[value] = getFormattedDate(currentValue)
            }
        })

        _notificationParams = {
            ..._notificationParams,
            "Recipient": "@Recipient",
            "Last updater": fullName || userEmail,
        }

        let languageSpecificData = {}
        if (template?.languageSpecific) {
            let uniqLangs = Object.keys(template.languageSpecific)
            uniqLangs.map(uniqLang => {
                let subject = subjectOverride || (template.languageSpecific[uniqLang].subject ? injectDataInTemplate(template.languageSpecific[uniqLang].subject, _notificationParams, formItems) : '')
                let content = injectDataInTemplate(template.languageSpecific[uniqLang].body, _notificationParams, formItems)
                if (subject) {
                    try {
                        const parser = new DOMParser();
                        let parsedSubject = parser.parseFromString(subject, 'text/html');
                        subject = parsedSubject.documentElement.innerText
                    } catch (error) {

                    }
                }
                if (content) {
                    try {
                        const parser = new DOMParser();
                        let parsedContent = parser.parseFromString(content, 'text/html');
                        if (parsedContent) {
                            const bodyElement = parsedContent.querySelector("body");
                            if (bodyElement) {
                                bodyElement.style.maxWidth = '650px';
                                bodyElement.style.margin = 'auto';
                            }
                            let anchorTag = parsedContent.querySelector(`[action-link="OPEN_THE_DOCUMENT"]`)
                            if (anchorTag) {
                                anchorTag.setAttribute("href", emailLink)
                            }
                            content = parsedContent.documentElement.innerHTML
                        }
                    } catch (error) {

                    }
                }
                languageSpecificData = {
                    ...languageSpecificData,
                    [uniqLang]: { subject, content }
                }
            })
        }

        return {
            redirectUrl,
            userId: userInfo?.userId || "",
            languageSpecific: languageSpecificData,
            sendEmail: true,
            to: [
                { recipientType: "user", data: recipients.user || [] },
                { recipientType: "group", data: recipients.group || [] }
            ],
            notificationType: template.notificationType,
            spaceId: spaceId
        }
    }

    // Build the notification payloads (one per enabled template) for the step the
    // request is advancing to, so the backend can dispatch emails after the
    // request is saved. Shared by the new-request and ongoing submission flows.
    const buildStepNotificationData = ({ stepStatus, dueDate, stepApprovers, stepNotifyTo, notificationRequestId, notificationValues, subjectOverride = "", requestCaseId = "" }) => {
        const companyIdentifier = normalizeCompanyIdentifier(workflow?.companyIdentifier)
        const redirectUrl = `/${companyIdentifier}/app/space/${workflow.spaceId}/approve/${workflow.workflowId}/${notificationRequestId}`
        const baseNotificationParams = {
            ...notificationValues,
            "Recipient": "[Recipient]",
            "Space name": formData?.spaceName,
            "Workflow step": stepStatus,
            "Due date": dueDate,
            "spaceId": workflow.spaceId,
            "Request Id": requestCaseId || formData?.caseId || ""
        }
        const notificationData = []

        const approverTemplate = dueDate
            ? notificationTemplates.find(notification => notification.notificationType === "WORKFLOW_STEP_WAITING_WITH_DUE_DATE")
            : notificationTemplates.find(notification => notification.notificationType === "WORKFLOW_STEP_WAITING")
        if (approverTemplate?.enabled) {
            const uniqueApprovers = getRequestApprovers(stepApprovers, notificationValues, [], [], previousPickerValues)
            if (uniqueApprovers.user?.length > 0 || uniqueApprovers.group?.length > 0) {
                notificationData.push(
                    buildNotificationPayload(approverTemplate, uniqueApprovers, baseNotificationParams, redirectUrl, formElements, subjectOverride)
                )
            }
        }

        const notifiedTemplate = notificationTemplates.find(notification => notification.notificationType === "WORKFLOW_STEP_NOTIFICATION")
        if (notifiedTemplate?.enabled) {
            const uniqueNotifiedUsers = getRequestApprovers(stepNotifyTo, notificationValues, [], [], previousPickerValues)
            if (uniqueNotifiedUsers.user?.length > 0 || uniqueNotifiedUsers.group?.length > 0) {
                notificationData.push(
                    buildNotificationPayload(notifiedTemplate, uniqueNotifiedUsers, baseNotificationParams, redirectUrl, formElements)
                )
            }
        }
        return notificationData
    }

    // Resolve the approvers configured on a single textInput step, handling both
    // the "default" (static assignees) and "conditional" approver types.
    const resolveApproversForNode = (node, values, extraFormItems = []) => {
        let approvers = { user: [], group: [], role: [], userPicker: [] }
        // Same inputs the signed-in request flow uses: earlier-step answers plus
        // this submission, and widgets from every workflow form so a condition can
        // read a field that is not on the public form itself.
        const conditionValues = {
            ...previousFormValues,
            ...(formData?.request?.value || {}),
            ...(formData?.requestDetail?.value || {}),
            ...(formData?.previousValues || {}),
            ...(formValues || {}),
            ...getLiveFormValues(),
            ...values,
        }
        const conditionElements = [
            ...previousFormItems,
            ...extraFormItems,
            ...flattenFormItems(formData?.workflowForms),
            ...(formData?.items || []),
            ...formElements,
        ]
        const nodeConditions = conditionsForNode(workflowConditions, node)
        // Conditions on the node win even when the public workflow payload still
        // says approverType "default" (or omits it). Otherwise the compare never runs.
        if (nodeConditions.length > 0) {
            let conditionStatus = nodeConditionsHandler(nodeConditions, conditionValues, conditionElements)
            if (conditionStatus?.length > 0) {
                conditionStatus.map(condition => {
                    let curApprovers = getRequestApprovers(condition.approvers, conditionValues, [], [], previousPickerValues)
                    for (const key in curApprovers) {
                        approvers[key] = Array.from(new Set([...approvers[key], ...curApprovers[key]]))
                    }
                })
            }
        } else if ((node?.data?.approverType || "default") === "default") {
            approvers = getRequestApprovers(node?.data?.assignTo || {}, conditionValues, [], [], previousPickerValues)
        }
        return approvers
    }

    // Follow the outgoing edges from the current step to the first downstream
    // textInput step that has resolvable approvers, returning that step and its
    // approvers. Shared by the new-request flow (starts at the default node) and
    // the ongoing flow (starts at the non-default step being submitted).
    const resolveNextStepApprovers = (values, fromNode, extraFormItems = []) => {
        let nextStep = {}
        let nextApprovers = { user: [], group: [], role: [], userPicker: [] }
        // Steps passed over on the way to nextStep, recorded so the request
        // timeline shows them as skipped instead of dropping them silently.
        let skippedSteps = []

        const nodes = getWorkflowNodes(workflow)
        const edges = getWorkflowEdges(workflow)
        const startNode = fromNode || nodes.find(node => node.type === "default")
        if (!startNode) return { nextStep, nextApprovers, skippedSteps }

        let cursorId = startNode.id
        const visited = new Set()
        while (cursorId && !visited.has(cursorId)) {
            visited.add(cursorId)
            const edge = edges.find(e => e.source === cursorId)
            if (!edge) break;
            const candidate = nodes.find(n => n.id === edge.target)
            if (!candidate) break;
            cursorId = candidate.id
            if (candidate.type !== "textInput") {
                if (candidate.data?.type === "end") {
                    nextStep = { ...candidate }
                    nextApprovers = { user: [], group: [], role: [], userPicker: [] }
                    break
                }
                continue;
            }
            const candidateApprovers = resolveApproversForNode(candidate, values, extraFormItems)
            if (isObjectEmpty(candidateApprovers, ["user", "group", "role"])) {
                skippedSteps.push(buildSkippedStepData(candidate))
                continue;
            }
            nextStep = { ...candidate }
            nextApprovers = candidateApprovers
            break;
        }
        // Every remaining input step had no approvers, and the walk never hit
        // the end node (last skipped step has no outgoing edge). Land on end
        // so the request is not left sitting on a step with nobody assigned.
        if (!nextStep?.id) {
            const endNode = nodes.find(n => n?.data?.type === "end")
            if (endNode) {
                nextStep = { ...endNode }
                nextApprovers = { user: [], group: [], role: [], userPicker: [] }
            }
        }
        return { nextStep, nextApprovers, skippedSteps }
    }

    // Follow the outgoing edges from the given step to the first downstream
    // textInput step, ignoring whether it has resolvable approvers. Used by the
    // ongoing flow to advance a non-approval step to the next input node even
    // when that node has no approvers configured.
    const resolveNextInputNode = (fromNode) => {
        const nodes = getWorkflowNodes(workflow)
        const startNode = fromNode || nodes.find(node => node.type === "default")
        if (!startNode) return null

        // Order the workflow nodes by their edges, then take the first textInput
        // step that comes after the current step.
        const workflowNodes = workflow?.node || []
        const nodeIds = new Set(workflowNodes.map(node => node.id))
        const workflowEdges = (workflow?.edge || []).filter(edge => nodeIds.has(edge.source) && nodeIds.has(edge.target))
        const sortedNodes = sortNodesByEdges(workflowNodes, workflowEdges)
        const startIndex = sortedNodes.findIndex(node => node.id === startNode.id)
        if (startIndex === -1) return null

        return sortedNodes.slice(startIndex + 1).find(node => node.type === "textInput") || null
    }

    // The step the submission belongs to: an ongoing link targets the node whose
    // form is being filled, while a public link always enters at the default node.
    const resolveCurrentStepNode = () => {
        if (type === "ongoing") {
            return workflowNodes.find(node => node.formData?.formId === formData?.formId) || null
        }
        return workflowNodes.find(node => node.type === "default") || null
    }

    // Renders the filled form to a structured PDF (section-wise label/value rows)
    // and stores it in the bucket, returning the object name so it can be recorded
    // on the request. Runs before the request is submitted: a failure here has to
    // leave the submission untouched so the user can simply press Submit again
    // without risking a duplicate request.
    const saveFormPrint = async (submitterName, values) => {
        const stepNode = resolveCurrentStepNode()
        // The canonical step name names the file and is recorded on the request;
        // the printed header shows the step in the form's own language instead.
        const stepName = stepNode?.data?.name
        const printStepName = resolveNodeTranslation(stepNode?.data, contentLanguage)?.name || stepName || ""
        const fileName = buildFormPrintFileName({
            stepName,
            userName: submitterName || userEmail,
            currentTime: moment(),
        })

        // Ongoing links always carry a saved request; public links only have one
        // when the form has attachment widgets, so ask the page to create it. Never
        // create one for an ongoing link — that would spawn a second request
        // alongside the one being progressed.
        //
        // Resolved before the document is rendered rather than after: the case ID is
        // printed on it, and it is only known once the request exists.
        let printRequestId = type === "ongoing" ? (requestId || formData?.requestId || "") : requestId
        let printTenantId = tenantId
        let printCaseId = caseId || formData?.caseId || formData?.request?.caseId || formData?.requestDetail?.caseId || ""
        if (!printRequestId && type !== "ongoing" && typeof ensureRequestRecord === "function") {
            const record = await ensureRequestRecord()
            printRequestId = record?.requestId || ""
            printTenantId = record?.tenantId || tenantId
            printCaseId = record?.caseId || printCaseId
        }

        if (!printRequestId) {
            throw new Error(t("Could not prepare the request for the form print"))
        }

        // Build the document from the definition + values, not the live DOM. Only
        // fields the submitter could see are included, mirroring the on-screen form.
        const visibleWidgets = getVisibleWidgets(formElements, { ...values }, {}, [], currentStatus, formConditions)
        const visibleIds = new Set(visibleWidgets.map((w) => w.id))
        const printFile = await generateStructuredFormPrintFile({
            formElements,
            values,
            lng: contentLanguage,
            visibleIds,
            fileName,
            meta: {
                title: formHeader?.label || t("Form Submission"),
                submittedByText: submitterName || userEmail
                    ? t("Submitted by {{name}}", { name: submitterName || userEmail })
                    : "",
                stepName: printStepName,
                // A numeric date rather than "Sep 8, 2026 3:21 PM": moment carries no
                // locale beyond English here, and this matches the DD-MM-YYYY the
                // form's own date fields print.
                date: moment().format("DD-MM-YYYY HH:mm"),
                caseId: printCaseId,
                caseIdLabel: t("Case ID"),
            },
        })

        const uploadResponse = await uploadPublicFormAttachment(printRequestId, shareToken, printFile, printTenantId)

        if (uploadResponse.status !== 200) {
            throw new Error(uploadResponse.message || t("Failed to save the form print"))
        }

        // The ids are handed back rather than read off props downstream: when the
        // draft was just created, setDocumentId/setTenantId have not re-rendered yet
        // and the props are still stale within this submission.
        return { fileName, stepName: stepName || "", requestId: printRequestId, tenantId: printTenantId, caseId: printCaseId }
    }

    const handleFormSubmission = async (values, submitterInfo = {}) => {
        const submitterName = submitterInfo.name ?? fullName ?? "";
        const submitterEmail = submitterInfo.email ?? userEmail ?? "";
        const submitterId = submitterInfo.id ?? userInfo?.userId ?? "";
        values = { ...getLiveFormValues(), ...(values || {}) };

        // Keep the loader up for the print upload and the request POST. Hiding it
        // after the attachment finished left a gap where submit ran with no overlay
        // — ongoing submissions never turned it back on at all.
        showLoader(true)
        try {
            // Capture the print first so nothing is submitted unless it succeeded.
            const formPrint = await saveFormPrint(submitterName, values)

        const formPrintRecord = {
            fileName: formPrint.fileName,
            stepName: formPrint.stepName,
            generatedOn: moment().utc(),
            generatedBy: submitterEmail,
        }

        // saveFormPrint may have just created the draft this submission belongs to,
        // and the requestId prop won't have caught up yet — using it here would POST
        // a second request and orphan the draft holding the print.
        // formPrint is an array so later steps can append their own prints; the
        // initial public submission seeds it with the one entry generated here.
        let postData = { requestId: formPrint.requestId || requestId, tenantId: formPrint.tenantId || tenantId, createdOn: moment().utc(), createdBy: { id: submitterId, name: submitterName, email: submitterEmail }, currentCycle: "1", spaceId: workflow.spaceId, workflowId: workflow.workflowId, status: "", value: { ...values }, dueDate: "", formPrint: [formPrintRecord] }
        let requestorFields = getRequestorFields(formData?.items)
        if (requestorFields?.length > 0) {
            requestorFields.map(field => {
                postData.value[field.id] = { user: [submitterEmail], group: [], role: [], userPicker: [] }
            })
        }

        let nextStep = {}, activityLogs = [], requestWorkflow = [], nextNotifyTo = { user: [], group: [], role: [], userPicker: [] }, nextApprovers = { user: [], group: [], role: [], userPicker: [] };
        // Visible fields (empty included) and hidden fields a condition sets.
        // Other hidden fields stay off this step so they are not frozen later.
        let currentValues = getStepFormValues(formElements, { ...values }, {}, [], currentStatus, formConditions)
        getAllWidgets(formElements).forEach((widget) => {
            if (widget?.formType !== "tableForm" || !widget?.id) return
            const rows = values[widget.id]
            if (Array.isArray(rows)) {
                currentValues[widget.id] = rows
            }
        })

        if (type === "ongoing") {
            let ongoingPostData = { requestId: formData?.requestId, formValues: { ...currentValues }, formPrint: formPrintRecord, status: { actionBy: submitterEmail, updatedOn: moment().utc(), approvalStatus: "submitted", comment: "submitted by external user", approverType: "user", approverValue: submitterEmail } }

            // Ongoing submissions happen on a non-default step. Identify that step
            // via the form being submitted, and only advance to the next step when
            // it does not require approval. When approvalStep is on, the request
            // stays on the current step awaiting approval and the current step's
            // assigned approvers are notified instead of the next step's.
            const currentStepNode = resolveCurrentStepNode()
            if (currentStepNode && !currentStepNode.data?.approvalStep) {
                // Same skip rules as a new public request: a next input step with
                // no resolvable approvers (failed/empty node conditions) is
                // bypassed rather than saved as the current pending step.
                const resolved = resolveNextStepApprovers(values, currentStepNode, previousFormItems)
                const nextInputNode = resolved.nextStep
                const landedOnEnd = nextInputNode?.data?.type === "end"
                if (resolved.skippedSteps?.length) {
                    ongoingPostData.skippedSteps = resolved.skippedSteps
                }
                if (landedOnEnd) {
                    ongoingPostData.nextStatus = nextInputNode.data?.name || "approved"
                    ongoingPostData.approvers = { user: [], group: [], role: [], userPicker: [] }
                    ongoingPostData.dueDate = ""
                    ongoingPostData.notificationData = buildStepNotificationData({
                        stepStatus: ongoingPostData.nextStatus,
                        dueDate: "",
                        stepApprovers: { user: [], group: [], role: [], userPicker: [] },
                        stepNotifyTo: { user: [], group: [], role: [], userPicker: [] },
                        notificationRequestId: formPrint.requestId || formData?.requestId,
                        notificationValues: values,
                        requestCaseId: formPrint.caseId || caseId || formData?.caseId || formData?.request?.caseId || formData?.requestDetail?.caseId || "",
                    })
                } else if (nextInputNode?.type === "textInput") {
                    const nextStepApprovers = resolved.nextApprovers
                    const nextStepNotifyTo = nextInputNode.data?.notifyTo
                        ? getRequestApprovers(nextInputNode.data.notifyTo, values, [], [], previousPickerValues)
                        : { user: [], group: [], role: [], userPicker: [] }
                    const nextDueDate = getDueDate(nextInputNode)

                    ongoingPostData.nextStatus = nextInputNode.data?.name
                    ongoingPostData.approvers = { ...nextStepApprovers }
                    if (nextInputNode.data?.notifyTo) {
                        ongoingPostData.notifyTo = { ...nextStepNotifyTo }
                    }
                    if (nextDueDate) {
                        ongoingPostData.dueDate = nextDueDate
                    }

                    // Build notification data for the next step so the backend can
                    // dispatch emails after the request advances (mirrors the
                    // new-request flow).
                    ongoingPostData.notificationData = buildStepNotificationData({
                        stepStatus: nextInputNode.data?.name,
                        dueDate: nextDueDate,
                        stepApprovers: nextStepApprovers,
                        stepNotifyTo: nextStepNotifyTo,
                        notificationRequestId: formPrint.requestId || formData?.requestId,
                        notificationValues: values,
                        requestCaseId: formPrint.caseId || caseId || formData?.caseId || formData?.request?.caseId || formData?.requestDetail?.caseId || "",
                    })
                }
            } else if (currentStepNode?.data?.approvalStep) {
                const currentStepApprovers = resolveApproversForNode(currentStepNode, values)
                const currentDueDate = formData?.request?.dueDate || formData?.dueDate || ""
                ongoingPostData.notificationData = buildStepNotificationData({
                    stepStatus: currentStepNode.data?.name,
                    dueDate: currentDueDate,
                    stepApprovers: currentStepApprovers,
                    stepNotifyTo: { user: [], group: [], role: [], userPicker: [] },
                    notificationRequestId: formPrint.requestId || formData?.requestId,
                    notificationValues: values,
                    subjectOverride: `${currentStepNode.data?.name || ""} form submitted`.trim(),
                    requestCaseId: formPrint.caseId || caseId || formData?.caseId || formData?.request?.caseId || formData?.requestDetail?.caseId || "",
                })
            }

            // Flag the backend to (re)calculate the assessment score when the
            // current form being submitted has scoring-enabled fields. Upcoming
            // forms are ignored.
            const ongoingHasScoringField = getAllWidgets(formData?.items || []).some((widget) => widget.enableScoring)
            if (ongoingHasScoringField) {
                ongoingPostData.calculateScore = true
            }

            let postResponse = await submitOngoingFormRequest(shareToken, ongoingPostData)
            if (postResponse.status === 200) {
                message.success(t('Request submitted'))
                form.resetFields();
                setDocumentId("")
                // Reset email verification states
                setShowEmailVerification(false);
                setCurrentStep(0);
                setUserEmail('');
                setFullName('');
                setVerificationToken('');
                emailForm.resetFields();
                otpForm.resetFields();
                setSubmitted(true);
            } else {
                message.error(postResponse.message || t('Failed to submit request. Please try again.'));
            }
            return
        }
        let defaultNode = workflowNodes.find(node => node.type === "default")

        let spaceResponse = await getSpaceAllPublicFormsByWorkflowId(workflow.workflowId, shareToken)
        let spaceForms = Array.isArray(spaceResponse?.data) ? spaceResponse.data : []
        const extraFormItems = flattenFormItems(spaceForms)

        const resolved = resolveNextStepApprovers(values, null, extraFormItems)
        nextStep = resolved.nextStep
        nextApprovers = resolved.nextApprovers
        let landedOnEnd = nextStep?.data?.type === "end"
        const hasResolvableApprovers = !isObjectEmpty(nextApprovers, ["user", "group", "role"])

        if (nextStep?.type === "textInput" && hasResolvableApprovers) {
            if (nextStep.data?.deadline) {
                postData.dueDate = getDueDate(nextStep)
            }
            postData.status = nextStep.data?.name
            if (nextStep?.data?.notifyTo) {
                nextNotifyTo = getRequestApprovers(nextStep?.data.notifyTo, values, [], [], previousPickerValues)
            }
            postData.approvers = { ...nextApprovers }
        } else if (landedOnEnd || !hasResolvableApprovers) {
            const endNode = workflowNodes.find(n => n?.data?.type === "end")
            if (!landedOnEnd && endNode) nextStep = { ...endNode }
            landedOnEnd = nextStep?.data?.type === "end"
            postData.status = nextStep?.data?.name || "approved"
            postData.approvers = { user: [], group: [], role: [], userPicker: [] }
            postData.dueDate = ""
        }
        let activityMessage = `${submitterName || submitterEmail || 'External user'} has submitted a public request`;
        activityLogs = activityLogs.concat({ "message": activityMessage, updatedOn: moment().utc(), updatedBy: submitterEmail })
        let reqStepData = { formValues: { ...currentValues }, ...defaultNode.data, dueDate: "", comment: "", name: defaultNode.data.name, count: defaultNode.data?.count, approvalStep: defaultNode.data?.approvalStep, period: defaultNode.data?.period, assignTo: defaultNode.data?.assignTo, formId: defaultNode.formData?.formId, approvalStatus: "submitted", delayed: false, actionBy: submitterEmail, actionOn: moment().utc() }
        requestWorkflow.push(reqStepData)
        // Steps bypassed for having no approvers sit between the submitted
        // requester step and the step the request actually lands on.
        requestWorkflow = requestWorkflow.concat([...(resolved.skippedSteps || [])])
        let nextStepData = landedOnEnd
            ? { ...nextStep.data, approvalStatus: "approved" }
            : {
                formValues: {},
                ...nextStep.data,
                dueDate: postData.dueDate,
                comment: "",
                name: postData.status,
                deadline: nextStep.data?.deadline,
                count: nextStep.data?.count,
                approvalStep: nextStep.data?.approvalStep,
                period: nextStep.data?.period,
                assignTo: nextApprovers,
                notifyTo: nextNotifyTo,
                formId: nextStep.formData?.formId,
                approvalStatus: "pending",
                actionBy: "",
                actionOn: "",
                delayed: false,
            }
        if (landedOnEnd || nextStep?.type === "textInput") {
            requestWorkflow.push(nextStepData)
        }
        postData["workflow"] = requestWorkflow
        postData["originalWorkflow"] = { nodes: workflow.node, edges: workflow.edge }
        postData["formConditions"] = [...formConditions]
        postData["workflowForms"] = [...spaceForms]
        postData["workflowConditions"] = [...workflowConditions]
        postData["activityLogs"] = [...activityLogs]

        // Flag the backend to calculate the assessment score when the current
        // form being submitted has scoring-enabled fields (upcoming forms are
        // ignored). Public submissions are always new requests, so there's no
        // existing score or draft state to guard against.
        const hasScoringField = getAllWidgets(formData?.items || []).some((widget) => widget.enableScoring)
        if (hasScoringField) {
            postData.calculateScore = true
        }

        // Build notification data for the next step so the backend can dispatch
        // emails after the request is created. Max two entries: next-step
        // approvers + notified users.
        postData["notificationData"] = buildStepNotificationData({
            stepStatus: postData.status,
            dueDate: postData.dueDate,
            stepApprovers: nextApprovers,
            stepNotifyTo: nextNotifyTo,
            // Same id the request is saved under (postData.requestId): the
            // requestId prop is still empty when saveFormPrint created the draft
            // during this submission, which would leave the email link
            // pointing at no request.
            notificationRequestId: postData.requestId,
            notificationValues: postData.value,
            requestCaseId: formPrint.caseId || caseId || formData?.caseId || formData?.request?.caseId || formData?.requestDetail?.caseId || "",
        })

        let postResponse = {}
        if (type === "ongoing") {
            let ongoingPostData = { requestId: formData?.requestId, name: submitterName, formValues: { ...currentValues }, formPrint: formPrintRecord, status: { actionBy: submitterEmail, updatedOn: moment().utc(), approvalStatus: "submitted", comment: "submitted by external user", approverType: "user", approverValue: submitterEmail } }
            postResponse = await submitOngoingFormRequest(shareToken, ongoingPostData)
        } else {
            postResponse = await submitPublicFormRequest(shareToken, postData)
        }
        if (postResponse.status === 200) {
            message.success(t('Request submitted'))
            form.resetFields();
            setDocumentId("")
            // Reset email verification states
            setShowEmailVerification(false);
            setCurrentStep(0);
            setUserEmail('');
            setFullName('');
            setVerificationToken('');
            emailForm.resetFields();
            otpForm.resetFields();
            setSubmitted(true);
            // Notifications for the next step are dispatched by the backend using
            // postData.notificationData (built above before submission).
        } else {
            message.error(postResponse.message || t('Failed to submit request. Please try again.'));
        }
        } catch (error) {
            const errMessage = error?.message || t('Failed to submit request. Please try again.')
            if (/print/i.test(errMessage)) {
                message.error(t('{{message}}. Please submit again.', { message: errMessage }))
            } else {
                message.error(errMessage)
            }
        } finally {
            showLoader(false)
        }
    };

    const normalizedType = String(type || '').trim().toLowerCase();
    const requiresOTPVerification = normalizedType !== "tracker" && !formData?.nonValidated;

    useEffect(() => {
        if (!requiresOTPVerification) {
            setShowEmailVerification(false);
            setCurrentStep(0);
        }
    }, [requiresOTPVerification]);

    // Drafts only make sense on an ongoing link: it already points at a saved
    // request sitting on a specific step, which is what the values get written
    // back onto. A public link has no request to keep open.
    const canSaveDraft = normalizedType === "ongoing" && Boolean(formData?.requestId || requestId);

    // Persist whatever the external user has filled in so far and leave the
    // request exactly where it is. Deliberately skips validation, the form print
    // and all next-step/approver resolution — nothing about the request moves,
    // only the values (and the current step's values in the workflow) are saved,
    // so the recipient can reopen the same link and carry on.
    const handleSaveDraft = async () => {
        if (!canSaveDraft) return;

        const rawValues = getLiveFormValues();
        const cleaned = trimStringValues(rawValues);
        const draftFormValues = getStepFormValues(formElements, cleaned, {}, [], currentStatus, formConditions)
        getAllWidgets(formElements).forEach((widget) => {
            if (widget?.formType !== "tableForm" || !widget?.id) return
            const rows = cleaned[widget.id]
            if (Array.isArray(rows)) {
                draftFormValues[widget.id] = rows
            }
        })

        const draftPostData = {
            requestId: formData?.requestId || requestId,
            formValues: draftFormValues,
            status: {
                actionBy: userEmail,
                updatedOn: moment().utc(),
                approvalStatus: "draft",
                comment: "saved as draft by external user",
                approverType: "user",
                approverValue: userEmail,
            },
        };

        setSavingDraft(true);
        showLoader(true);
        try {
            const draftResponse = await saveOngoingFormDraft(shareToken, draftPostData);
            if (draftResponse.status === 200) {
                setDraftSavedOn(moment());
                message.success(t('Draft saved. You can reopen this link later to continue.'));
            } else {
                message.error(draftResponse.message || t('Failed to save draft. Please try again.'));
            }
        } catch (error) {
            message.error(error.message || t('Failed to save draft. Please try again.'));
        } finally {
            showLoader(false);
            setSavingDraft(false);
        }
    };

    const handleSubmit = async () => {
        try {
            // Instead of validating raw values first, normalize (trim) them,
            // write them back into the form and then validate so whitespace-only
            // inputs (including number fields submitted as strings of spaces)
            // are treated as empty.
            const rawValues = getLiveFormValues();
            const cleaned = trimStringValues(rawValues);

            // Convert empty strings for numeric widgets into undefined so
            // AntD validators (and backend) treat them as empty.
            const numberFieldIds = (Array.isArray(formElements) ? formElements : [])
                .filter(f => f.formType === 'numberForm')
                .map(f => f.id);
            numberFieldIds.forEach(id => {
                if (cleaned[id] === '') cleaned[id] = undefined;
            });

            if (Object.keys(cleaned).length > 0) {
                await form.setFieldsValue(cleaned);
                mergeFilledValues(cleaned);
            }
            await form.validateFields();

            if (!userEmail || !fullName) {
                setShowEmailVerification(true);
                setCurrentStep(0);
                return;
            }

            // Check if email verification is required
            if (requiresOTPVerification && !verificationToken) {
                setShowEmailVerification(true);
                setCurrentStep(1);
                return;
            }

            // If already verified, submit directly
            setSubmitting(true);
            try {
                await handleFormSubmission(cleaned, { name: fullName, email: userEmail });
                setShowEmailVerification(false);
            } finally {
                setSubmitting(false);
            }
        } catch (errorInfo) {
            setShowEmailVerification(false);
            message.error(t('Please fill all required fields correctly.'));
            if (errorInfo?.errorFields?.length) {
                scrollToFirstMissingField(form, errorInfo);
            }
        }
    };

    if (!formData) {
        return null;
    }

    if (submitted) {
        return (
            <div className="w-full flex flex-col items-center justify-center py-16 space-y-6">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircleOutlined className="text-4xl text-green-500" />
                </div>
                <Title level={3} className="!mb-0 text-center">
                    {t('Your request has been submitted')}
                </Title>
                <Paragraph className="text-gray-500 text-center text-base">
                    {t('Thank you for your submission.')}
                </Paragraph>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6">
            <Modal
                title={
                    <div className="flex items-center space-x-2">
                        {currentStep === 0 ? <MailOutlined /> : currentStep === 1 ? <SafetyOutlined /> : <SendOutlined />}
                        <span>
                            {currentStep === 0 ? t('Email Verification') : currentStep === 1 ? t('Enter OTP') : t('Submit Form')}
                        </span>
                    </div>
                }
                open={showEmailVerification}
                onCancel={() => setShowEmailVerification(false)}
                footer={null}
                width={440}
                centered
            >
                <div className="py-4">
                    {requiresOTPVerification && (
                        <Steps
                            current={currentStep}
                            size="small"
                            className="mb-6"
                            items={[
                                { title: t('Email') },
                                { title: t('OTP') },
                                { title: t('Submit') },
                            ]}
                        />
                    )}

                    {currentStep === 0 && (
                        <Form
                            form={emailForm}
                            layout="vertical"
                            onFinish={handleEmailSubmission}
                        >

                            <Form.Item
                                name="name"
                                label={t('Full Name')}
                                rules={[
                                    { required: true, whitespace: true, message: t('Please enter your full name') }
                                ]}
                            >
                                <Input
                                    prefix={<UserOutlined />}
                                    placeholder={t('Enter your full name')}
                                    size="large"
                                />
                            </Form.Item>

                            <Form.Item
                                name="email"
                                label={t('Email Address')}
                                rules={[
                                    { required: true, whitespace: true, message: t('Please enter your email') },
                                    { type: 'email', message: t('Please enter a valid email') }
                                ]}
                            >
                                <Input
                                    prefix={<MailOutlined />}
                                    placeholder={t('Enter your email address')}
                                    size="large"
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={sendingOTP}
                                    size="large"
                                    className="w-full success-button transition-all duration-300 shadow-lg hover:shadow-xl"
                                >
                                    {requiresOTPVerification ? t('Send OTP') : t('Continue')}
                                </Button>
                            </Form.Item>
                        </Form>
                    )}

                    {requiresOTPVerification && currentStep === 1 && (
                        <Form
                            form={otpForm}
                            layout="vertical"
                            onFinish={handleOTPVerification}
                        >
                            <div className="text-center mb-4">
                                <p className="text-gray-600">
                                    {t("We've sent a verification code to")}<br />
                                    <strong>{userEmail}</strong>
                                </p>
                            </div>

                            <Form.Item
                                name="otp"
                                label={t('Verification Code')}
                                rules={[
                                    { required: true, whitespace: true, message: t('Please enter the OTP') },
                                    { len: 6, message: t('OTP must be 6 digits') }
                                ]}
                            >
                                <Input
                                    prefix={<SafetyOutlined />}
                                    placeholder={t('Enter 6-digit OTP')}
                                    size="large"
                                    maxLength={6}
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={verifyingOTP}
                                    size="large"
                                    className="w-full success-button transition-all duration-300 shadow-lg hover:shadow-xl"
                                >
                                    {t('Verify OTP')}
                                </Button>
                            </Form.Item>

                            <div className="text-center">
                                <Button
                                    type="link"
                                    onClick={() => setCurrentStep(0)}
                                    className="text-[#141bcb]"
                                >
                                    {t('Change Email')}
                                </Button>
                            </div>
                        </Form>
                    )}

                    {requiresOTPVerification && currentStep === 2 && (
                        <div className="text-center">
                            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                                <CheckCircleOutlined className="text-2xl text-green-500" />
                            </div>
                            <p className="text-gray-600 mb-1">
                                {t('Email verified successfully!')}
                            </p>
                            {fullName ? (
                                <p className="font-medium text-gray-900 mb-0">{fullName}</p>
                            ) : null}
                            <p className="font-medium text-gray-900 mb-6">{userEmail}</p>
                            <Button
                                type="primary"
                                size="large"
                                icon={<SendOutlined />}
                                loading={submitting}
                                onClick={handleSubmit}
                                className="w-full success-button transition-all duration-300 shadow-lg hover:shadow-xl"
                            >
                                {submitting ? t('Submitting...') : t('Submit Form')}
                            </Button>
                        </div>
                    )}
                </div>
            </Modal>

            {displayCaseId ? (
                <div className="w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg bg-[#f8f9ff] border border-[#e4e6fb]">
                    <span className="text-xs font-semibold tracking-wide uppercase text-gray-500">
                        {t('Case ID')}
                    </span>
                    <span className="text-base font-semibold text-gray-900 break-all">
                        {displayCaseId}
                    </span>
                </div>
            ) : null}

            {/* Form Header */}
            {!formHeader.hideHeader && (
                <div className="w-full">
                    <Title level={2} className=" !mb-4 font-semibold">
                        {formHeader.label || t('Form')}
                    </Title>

                    {formHeader.secondaryText && formHeader.description && (
                        <Paragraph className="text-gray-700 mb-4">
                            {formHeader.description}
                        </Paragraph>
                    )}

                    {formHeader.info && formHeader.formInfo && (
                        <Alert
                            message={formHeader.formInfo}
                            type="info"
                            showIcon
                            className="mb-6 border-[#141bcb]/20 bg-gradient-to-r from-blue-50 to-indigo-50"
                        />
                    )}
                </div>
            )}

            {/* Form Body */}
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                onValuesChange={(changedValues) => {
                    mergeFilledValues(changedValues || {});
                }}
                onFieldsChange={(changedFields) => {
                    const next = {}
                    changedFields.forEach((field) => {
                        const name = Array.isArray(field.name) ? field.name[0] : field.name
                        if (name != null && field.value !== undefined) next[name] = field.value
                    })
                    if (Object.keys(next).length) mergeFilledValues(next)
                }}
                className="w-full"
                size="large"
                scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
            >
                <div className="w-full flex flex-wrap">
                    {formElements.map((element, index) => (
                        <Wrapper
                            key={element.id || index}
                            data={element}
                            form={form}
                            lng={contentLanguage}
                            formValues={sliceRef.current(element, formValues, formConditions)}
                            conditions={formConditions}
                            isPublicForm={true}
                            formElements={formElements}
                            allFormElements={allFormElements}
                            spaceData={spaceData}
                            currentStatus={currentStatus}
                            freezeFields={freezeFields}
                            previewMode={true}
                            requestId={requestId}
                            spaceId={workflow.spaceId}
                            tenantId={tenantId}
                            shareToken={shareToken}
                            defaultFormValues={defaultFormValues}
                            workflowNodes={workflowNodes}
                            workflowEdges={workflowEdges}
                            passFormValues={mergeFilledValues}
                        />
                    ))}
                </div>

                {/* Submit Button */}
                <div className="w-full mt-10 pt-6 border-t border-gray-200">
                    {requiresOTPVerification && verificationToken && (
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center space-x-2 text-green-700">
                                <SafetyOutlined />
                                <span className="font-medium">{t('Email Verified:')} {userEmail}</span>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap items-center gap-3">
                        <Button
                            type="primary"
                            size="large"
                            icon={<SendOutlined />}
                            loading={submitting}
                            disabled={savingDraft}
                            onClick={handleSubmit}
                            className="success-button transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                            {submitting ? t('Submitting...') : !userEmail || !fullName ? t('Enter Details & Submit') : requiresOTPVerification ? (verificationToken ? t('Submit Form') : t('Verify Email & Submit')) : t('Submit Form')}
                        </Button>

                        {canSaveDraft && (
                            <Button
                                size="large"
                                icon={<SaveOutlined />}
                                loading={savingDraft}
                                disabled={submitting}
                                onClick={handleSaveDraft}
                                className="transition-all duration-300"
                            >
                                {savingDraft ? t('Saving...') : t('Save as Draft')}
                            </Button>
                        )}
                    </div>

                    {canSaveDraft && (
                        <p className="mt-3 mb-0 text-sm text-gray-500">
                            {draftSavedOn
                                ? t('Draft saved at {{time}}. Reopen this link to continue.', { time: draftSavedOn.format('DD MMM YYYY, HH:mm') })
                                : t('Saving as a draft keeps your answers without submitting the form.')}
                        </p>
                    )}
                </div>
            </Form>
        </div>
    );
};

export default PublicForm; 
